<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import { svgIcon, baseInput } from "@/common/components";

import { laboratoryList } from "@/common/api/rest/laboratory";

import type { LaboratoryListType } from "@/common/types/laboratory";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = defineProps<{
  title?: string;
  account_id?: string;
  ids?: string[];
}>();

const selecteds = ref<any[]>([]);
const listData = ref<LaboratoryListType[]>([]);
const inputSearch = ref<string | null>("");

function search() {
  if (!props.account_id) return;

  laboratoryList(props.account_id, {
    search: inputSearch.value as string,
    limit: 500,
    page: 0,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        listData.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function select(data: any) {
  if (props.ids?.includes(data.laboratory_id)) return;

  if (selecteds.value.find((sl) => sl.laboratory_id === data.laboratory_id)) {
    selecteds.value = selecteds.value.filter(
      (sl) => sl.laboratory_id !== data.laboratory_id,
    );
  } else {
    selecteds.value.push(data);
  }

  emit("update-data", selecteds.value);
}

onMounted(() => {
  search();
});
</script>

<template>
  <section class="technical-form">
    <div class="title">
      {{ title }}
    </div>
    <div class="search">
      <base-input
        v-model="inputSearch"
        background="#fff"
        color="#000"
        :placeholder="t('common.labels.search')"
        @key:enter="search()"
      >
        <template #sufix>
          <div class="icon">
            <svg-icon icon="search" size="1.75rem" @click="search()" />
          </div>
        </template>
      </base-input>
    </div>

    <div class="list">
      <template v-for="(item, index) in listData" :key="index">
        <div
          class="item"
          @click="select(item)"
          :class="{
            active: selecteds.find(
              (sl) => sl.laboratory_id === item.laboratory_id,
            ),
            disabled: ids?.find((id) => id === item.laboratory_id),
          }"
        >
          <div class="name">
            {{ item.name }}
          </div>
          <div class="type">
            {{ item.document_name }} - {{ item.document_number }}
          </div>
        </div>
      </template>
      <template v-if="listData.length === 0">
        <div class="empty">
          {{ t("common.texts.no_results") }}
        </div>
      </template>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.technical-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .list {
    display: flex;
    flex-direction: column;
    width: calc(100% - 0.5rem);
    height: 50dvh;
    overflow-y: scroll;
    padding-inline-end: 0.5rem;
    gap: 0.5rem;

    .item {
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      border: 1px solid var(--border);
      padding-block: 0.5rem;
      padding-inline: 1rem;
      border-radius: 0.5rem;

      &.active {
        border-color: var(--primary);
        background: var(--primary);
        color: var(--primary-color);
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .name {
        font-weight: bold;
      }
    }
  }
}
</style>
